import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import orbUrl from '../orbAsset';

const wordmarkUrl = "/hypseya-wordmark.png";

const TEAL = '#1E6B68';
const TEAL_LIGHT = '#2D8B87';

function Section({ children, style = {} }) {
  return (
    <div style={{
      maxWidth: '640px',
      margin: '0 auto',
      padding: '0 24px',
      ...style
    }}>
      {children}
    </div>
  );
}

function Divider() {
  return (
    <div style={{
      width: '40px', height: '2px',
      background: `linear-gradient(90deg, ${TEAL}, transparent)`,
      margin: '48px auto'
    }} />
  );
}

function PillTag({ label }) {
  return (
    <span style={{
      display: 'inline-block',
      padding: '4px 14px',
      background: `rgba(30, 107, 104, 0.08)`,
      border: `1px solid rgba(30, 107, 104, 0.2)`,
      borderRadius: '20px',
      fontSize: '0.78rem',
      color: TEAL,
      fontWeight: '500',
      letterSpacing: '0.03em',
      margin: '4px'
    }}>
      {label}
    </span>
  );
}

function LoopCard({ number, label, desc, delay }) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), delay);
    return () => clearTimeout(t);
  }, [delay]);

  return (
    <div style={{
      display: 'flex', gap: '16px', alignItems: 'flex-start',
      opacity: visible ? 1 : 0,
      transform: visible ? 'translateY(0)' : 'translateY(16px)',
      transition: 'opacity 0.5s ease, transform 0.5s ease',
      marginBottom: '20px'
    }}>
      <div style={{
        width: '36px', height: '36px', borderRadius: '50%', flexShrink: 0,
        background: `linear-gradient(135deg, ${TEAL} 0%, ${TEAL_LIGHT} 100%)`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: '#fff', fontSize: '0.9rem', fontWeight: '600'
      }}>
        {number}
      </div>
      <div>
        <p style={{
          margin: '0 0 4px',
          fontFamily: "'Playfair Display', serif",
          fontSize: '1.05rem', color: TEAL, fontWeight: '600'
        }}>{label}</p>
        <p style={{ margin: 0, color: '#666', fontSize: '0.9rem', lineHeight: '1.6' }}>{desc}</p>
      </div>
    </div>
  );
}

function ERIDimension({ label, desc }) {
  return (
    <div style={{
      padding: '16px 20px',
      background: 'rgba(255,255,255,0.7)',
      borderRadius: '14px',
      borderLeft: `3px solid ${TEAL}`,
      marginBottom: '12px'
    }}>
      <p style={{ margin: '0 0 4px', color: TEAL, fontWeight: '600', fontSize: '0.95rem' }}>{label}</p>
      <p style={{ margin: 0, color: '#666', fontSize: '0.87rem', lineHeight: '1.5' }}>{desc}</p>
    </div>
  );
}

export default function About() {
  const navigate = useNavigate();

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(160deg, #fafbfc 0%, #f0f7f6 35%, #e8f3f1 70%, #f5f8f7 100%)',
      paddingBottom: '120px'
    }}>

      {/* Hero */}
      <div style={{
        textAlign: 'center',
        padding: '80px 24px 48px',
        background: 'linear-gradient(180deg, rgba(30,107,104,0.04) 0%, transparent 100%)'
      }}>
        <div className="hyp-orb-wrapper" style={{ width: '80px', height: '80px', margin: '0 auto 20px' }}>
          <span className="hyp-ripple-1" />
          <span className="hyp-ripple-2" />
          <img src={orbUrl} alt="HYPSEYA" style={{ width: '100%' }} />
        </div>
        <img src={wordmarkUrl} alt="HYPSEYA" style={{ width: '160px', display: 'block', margin: '0 auto 24px' }} />
        <p style={{
          fontFamily: "'Playfair Display', serif",
          fontSize: 'clamp(1.1rem, 3vw, 1.35rem)',
          color: TEAL, lineHeight: '1.7',
          maxWidth: '480px', margin: '0 auto',
          fontWeight: '400', fontStyle: 'italic'
        }}>
          A structured practice for observing<br />
          and reshaping internal patterns.
        </p>
      </div>

      {/* What is HYPSEYA */}
      <Divider />
      <Section>
        <p style={{ fontSize: '0.75rem', color: TEAL_LIGHT, textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '12px' }}>
          What is HYPSEYA
        </p>
        <h2 style={{
          fontFamily: "'Playfair Display', serif",
          fontSize: 'clamp(1.5rem, 4vw, 2rem)',
          color: '#1a3a38', margin: '0 0 20px', lineHeight: '1.3'
        }}>
          Emotional technology.<br />Not a wellness app.
        </h2>
        <p style={{ color: '#555', lineHeight: '1.85', fontSize: '0.97rem', marginBottom: '16px' }}>
          HYPSEYA is a digital ecosystem designed to map, measure and transform internal states. It combines emotional, somatic, cognitive and behavioural data to create a living portrait of each person — revealing patterns in how they think, react, and regulate.
        </p>
        <p style={{ color: '#555', lineHeight: '1.85', fontSize: '0.97rem' }}>
          The question at its core is simple: <strong style={{ color: TEAL }}>how do we measure internal transformation in a structured way?</strong>
        </p>
      </Section>

      {/* Tags */}
      <div style={{ textAlign: 'center', padding: '32px 24px 0' }}>
        {[
          'Emotional Training', 'Pattern Recognition', 'Neuroplasticity',
          'Somatic Tracking', 'Therapeutic Bridge', 'Clinical Research'
        ].map(t => <PillTag key={t} label={t} />)}
      </div>

      <Divider />

      {/* Neuroplasticity */}
      <Section>
        <p style={{ fontSize: '0.75rem', color: TEAL_LIGHT, textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '12px' }}>
          The Science Behind It
        </p>
        <h2 style={{
          fontFamily: "'Playfair Display', serif",
          fontSize: 'clamp(1.3rem, 3.5vw, 1.7rem)',
          color: '#1a3a38', margin: '0 0 20px', lineHeight: '1.3'
        }}>
          Neuroplasticity applied.
        </h2>
        <p style={{ color: '#555', lineHeight: '1.85', fontSize: '0.97rem', marginBottom: '16px' }}>
          If the brain is malleable, then we can design experiences that reinforce new neural patterns. HYPSEYA combines guided journaling, emotional tracking, voice, breathing, intention and integration protocols to create <strong style={{ color: TEAL }}>conscious repetition</strong>.
        </p>
        <p style={{ color: '#555', lineHeight: '1.85', fontSize: '0.97rem' }}>
          It's not about feeling better in a moment. It's about understanding the process of transformation — and making it visible over time.
        </p>
      </Section>

      <Divider />

      {/* Daily Loop */}
      <Section>
        <p style={{ fontSize: '0.75rem', color: TEAL_LIGHT, textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '12px' }}>
          Daily Loop
        </p>
        <h2 style={{
          fontFamily: "'Playfair Display', serif",
          fontSize: 'clamp(1.3rem, 3.5vw, 1.7rem)',
          color: '#1a3a38', margin: '0 0 8px', lineHeight: '1.3'
        }}>
          Four steps. Every day.
        </h2>
        <p style={{ color: '#888', fontSize: '0.9rem', marginBottom: '28px', lineHeight: '1.6' }}>
          Each cycle transforms daily practice into a continuous process of attention, experience, observation and integration.
        </p>
        <LoopCard number="1" label="Orient" desc="Set your intention. Capture your activation state before practice begins." delay={100} />
        <LoopCard number="2" label="Practice" desc="A guided experience — meditation, breathwork, or somatic practice." delay={250} />
        <LoopCard number="3" label="Observe" desc="Notice what shifted. Track activation, body tension, and clarity after." delay={400} />
        <LoopCard number="4" label="Integrate" desc="Anchor the experience. Carry the pattern into your day." delay={550} />
      </Section>

      <Divider />

      {/* ERI */}
      <Section>
        <p style={{ fontSize: '0.75rem', color: TEAL_LIGHT, textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '12px' }}>
          Emotional Regulation Index
        </p>
        <h2 style={{
          fontFamily: "'Playfair Display', serif",
          fontSize: 'clamp(1.3rem, 3.5vw, 1.7rem)',
          color: '#1a3a38', margin: '0 0 8px', lineHeight: '1.3'
        }}>
          Measure what matters.
        </h2>
        <p style={{ color: '#888', fontSize: '0.9rem', marginBottom: '24px', lineHeight: '1.6' }}>
          The ERI is not a diagnostic. It is a mirror — a way of seeing your own evolution over time, built from three dimensions:
        </p>
        <ERIDimension label="Regulation Capacity" desc="How effectively you down-regulate arousal through practice. Measured by the delta between pre and post-activation states." />
        <ERIDimension label="Emotional Stability" desc="The consistency of your somatic and clarity responses across sessions. Less variance means growing stability." />
        <ERIDimension label="Practice Consistency" desc="The regularity of your engagement over time. Transformation requires repetition." />
        <p style={{ color: '#888', fontSize: '0.85rem', marginTop: '16px', fontStyle: 'italic', lineHeight: '1.6' }}>
          Over time, these three dimensions form a map of your internal capacity — and reveal how it changes.
        </p>
      </Section>

      <Divider />

      {/* Voice */}
      <Section>
        <p style={{ fontSize: '0.75rem', color: TEAL_LIGHT, textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '12px' }}>
          Voice Technology
        </p>
        <h2 style={{
          fontFamily: "'Playfair Display', serif",
          fontSize: 'clamp(1.3rem, 3.5vw, 1.7rem)',
          color: '#1a3a38', margin: '0 0 20px', lineHeight: '1.3'
        }}>
          Your voice. Regulated.
        </h2>
        <p style={{ color: '#555', lineHeight: '1.85', fontSize: '0.97rem' }}>
          HYPSEYA can clone your voice and transform it into a state of regulation. Instead of listening to a stranger, you listen to yourself — calm, steady, present. This intimate quality accelerates internalisation and makes each practice feel unmistakably personal.
        </p>
      </Section>

      <Divider />

      {/* Therapeutic Bridge */}
      <Section>
        <p style={{ fontSize: '0.75rem', color: TEAL_LIGHT, textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '12px' }}>
          Clinical Context
        </p>
        <h2 style={{
          fontFamily: "'Playfair Display', serif",
          fontSize: 'clamp(1.3rem, 3.5vw, 1.7rem)',
          color: '#1a3a38', margin: '0 0 20px', lineHeight: '1.3'
        }}>
          A bridge between<br />technology and therapy.
        </h2>
        <p style={{ color: '#555', lineHeight: '1.85', fontSize: '0.97rem', marginBottom: '16px' }}>
          HYPSEYA is designed to integrate with therapeutic contexts — allowing practitioners, clinics and researchers to observe patterns of practice, regulation evolution and user reflections between sessions.
        </p>
        <p style={{ color: '#555', lineHeight: '1.85', fontSize: '0.97rem', marginBottom: '16px' }}>
          It also supports integration after intense or non-ordinary experiences — breathwork, somatic work, psychedelic-assisted therapy. After a transformative experience, the platform offers structure to observe internal states over time, stabilise regulation, and transform insights into sustainable patterns.
        </p>
        <p style={{ color: '#888', fontSize: '0.85rem', fontStyle: 'italic', lineHeight: '1.6' }}>
          HYPSEYA does not induce, guide or replace any therapeutic experience.<br />
          Its role is exclusively to support integration, observation and stabilisation over time.
        </p>
      </Section>

      <Divider />

      {/* Vision */}
      <Section>
        <p style={{ fontSize: '0.75rem', color: TEAL_LIGHT, textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '12px' }}>
          Long-term Vision
        </p>
        <h2 style={{
          fontFamily: "'Playfair Display', serif",
          fontSize: 'clamp(1.3rem, 3.5vw, 1.7rem)',
          color: '#1a3a38', margin: '0 0 20px', lineHeight: '1.3'
        }}>
          Emotional infrastructure<br />for the digital age.
        </h2>
        <p style={{ color: '#555', lineHeight: '1.85', fontSize: '0.97rem', marginBottom: '20px' }}>
          The long-term vision is to position HYPSEYA as a digital emotional infrastructure — collaborating with universities, research centres, mental health clinics, therapeutic training programmes, and foundations working in therapeutic innovation.
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          {['Universities', 'Mental Health Clinics', 'Research Centres', 'Therapeutic Training', 'Psychedelic Medicine', 'Somatic Practitioners'].map(t => <PillTag key={t} label={t} />)}
        </div>
      </Section>

      {/* CTA */}
      <div style={{ textAlign: 'center', padding: '64px 24px 0' }}>
        <p style={{
          fontFamily: "'Playfair Display', serif",
          fontSize: '1.3rem', color: TEAL,
          marginBottom: '28px', fontStyle: 'italic'
        }}>
          The transformation happens through<br />conscious repetition over time.
        </p>
        <button
          onClick={() => navigate('/')}
          style={{
            padding: '16px 48px',
            background: `linear-gradient(135deg, ${TEAL} 0%, ${TEAL_LIGHT} 100%)`,
            color: '#fff', border: 'none', borderRadius: '32px',
            fontSize: '1rem', fontWeight: '600', cursor: 'pointer',
            boxShadow: '0 4px 20px rgba(30, 107, 104, 0.3)'
          }}
        >
          Begin Your Practice
        </button>
      </div>

      {/* orb animation handled by hypseya-brand.css */}
    </div>
  );
}
